import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import RadioButton from 'components/buttons/RadioButton';
import FormStepWrapper from 'components/form/FormStepWrapper';
import { ItemWeightSelector } from 'components/item/item-weight-selector/ItemWeightSelector';
import DropoffPlace from 'components/location/dropoff-place/DropoffPlace';
import LocationField from 'components/location/picker/LocationField';
import { LocationPickerStep } from 'components/location/picker/LocationPickerStep';
import { useAddItem } from 'features/add-item/context/AddItemProvider';
import ShipFromAddress from 'features/add-item/form/components/ship-from-address/ShipFromAddress';
import { useCurrentUser } from 'providers/cache/cachedDataHooks';
import { fromUserLocation } from 'services/utils/location/locationPicker.utils';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 12px;
`;

const RadioButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: start;
`;

export const SetItemDeliveryContent = ({ onOpenLocationStep }: { onOpenLocationStep?: () => void }) => {
  const { t } = useTranslation('addItem');
  const { formData, setFieldValue, isEditMode } = useAddItem();
  return (
    <Container>
      <LocationField
        mode={onOpenLocationStep ? 'step' : 'popup'}
        value={formData.itemLocation}
        onChange={value => setFieldValue('itemLocation', value)}
        onOpenStep={onOpenLocationStep}
        placeholder={t('common:set-location')}
      />
      <RadioButtonsContainer>
        <RadioButton
          data-testid={'radio-local-pickup'}
          checked={formData.canLocalPickUpDelivery}
          onChange={() => {}}
          onClick={() => setFieldValue('canLocalPickUpDelivery', !formData.canLocalPickUpDelivery)}>
          {t('common:local-pickup')}
        </RadioButton>
        <RadioButton
          data-testid={'radio-nationwide-shipment'}
          checked={formData.canNationwideShipping}
          onChange={() => {}}
          onClick={() => setFieldValue('canNationwideShipping', !formData.canNationwideShipping)}>
          {t('common:nationwide-shipping')}
        </RadioButton>
      </RadioButtonsContainer>
      {formData.sellingMethod === 'MULTIPLE' && formData.canNationwideShipping && isEditMode && (
        <ShipFromAddress
          addressId={formData.defaultShipFromAddressId}
          onAddressSelected={addressId => setFieldValue('defaultShipFromAddressId', addressId)}
        />
      )}
      {formData.canNationwideShipping && (
        <ItemWeightSelector
          selectedWeightUpTo={formData.weightUpTo}
          onWeightSelected={weightUpTo => setFieldValue('weightUpTo', weightUpTo)}
        />
      )}
      {formData.canNationwideShipping && !!formData.weightUpTo && <DropoffPlace location={formData.itemLocation} />}
    </Container>
  );
};

const SetItemDelivery = () => {
  const { t } = useTranslation('addItem');
  const { formData, errors, setFieldValue, isEditMode } = useAddItem();
  const { currentUser } = useCurrentUser();
  const [isPickingLocation, setIsPickingLocation] = useState(false);
  const isValid = !errors.itemLocation && !errors.canNationwideShipping && !errors.weightUpTo;

  useEffect(() => {
    if (isEditMode || formData.itemLocation) return;
    const profileLocation = fromUserLocation(currentUser?.location);
    if (profileLocation) setFieldValue('itemLocation', profileLocation);
  }, [isEditMode, formData.itemLocation, currentUser?.location, setFieldValue]);

  if (isPickingLocation) {
    return (
      <LocationPickerStep
        defaultLocation={formData.itemLocation}
        onCancel={() => setIsPickingLocation(false)}
        onConfirm={location => {
          setFieldValue('itemLocation', location);
          setIsPickingLocation(false);
        }}
      />
    );
  }

  return (
    <FormStepWrapper
      header={t('delivery.regular.header')}
      description={t('delivery.regular.description')}
      isValid={isValid}>
      <SetItemDeliveryContent onOpenLocationStep={() => setIsPickingLocation(true)} />
    </FormStepWrapper>
  );
};

export default SetItemDelivery;
